import { IClientOption, IClientOptionByTeam, IClientOptionTeam } from '~/store/client/types';
import { IAddClientAllocation } from '~/services/api/clientAllocation';
import { IAllocationClientsByGroup } from '~/services/api/clientAllocation/types';
import { IOption } from '~/types';

import {
  ClientWithAllocation,
  LocationGroupWithAllocation,
  NormalizedTeamMembersAllocations,
} from '../shared/model/types';

const getAllocationId = (allocation: ClientWithAllocation): number | null =>
  allocation.allocation?.id || allocation.allocationId || null;

export const getSameAllocationFieldsCount = (
  allocations: ClientWithAllocation[],
  allocation: ClientWithAllocation,
): number => {
  const allocationId = getAllocationId(allocation);

  return allocations.filter(item => getAllocationId(item) === allocationId).length;
};

const resolveTeam = (
  teams?: Partial<IClientOptionTeam>[] | null,
  team?: Partial<IClientOptionTeam> | null,
): Partial<IClientOptionTeam> | null => team ?? teams?.[0] ?? null;

const normalizeClientOptionToAllocation = (
  option: IOption,
  existedAllocationId?: number | null,
): ClientWithAllocation => ({
  id: Number(option.value),
  name: option.name ?? option.label,
  photo: option.photo ?? '',
  userId: option.value as number,
  memo: option.memo ?? '',
  team: resolveTeam(option.teams),
  allocationId: existedAllocationId || null,
  allocation: null,
  clientId: Number(option.value),
  teams: option.teams,
});

const normalizeClientOptionByTeamToAllocation = (
  option: IClientOptionByTeam | IClientOption | ClientWithAllocation,
  existedAllocationId?: number | null,
): ClientWithAllocation => ({
  id: option.id,
  name: option.name,
  photo: option.photo ?? '',
  memo: '',
  userId: option.id,
  team: resolveTeam('teams' in option ? option.teams ?? null : null, option.team ?? null),
  allocation: null,
  allocationId: existedAllocationId ?? null,
  clientId: option.id,
  // eslint-disable-next-line no-nested-ternary
  teams: 'teams' in option ? option.teams ?? null : option.team ? [option.team] : null,
});

export const normalizeClientOptionsToAllocations = (
  options: IOption[],
  existedAllocationId: number,
): ClientWithAllocation[] =>
  options.map(option => normalizeClientOptionToAllocation(option, existedAllocationId));

export const normalizeClientOptionsByTeamToAllocations = (
  options: Array<IClientOptionByTeam | IClientOption | ClientWithAllocation>,
  existedAllocationId: number | null,
): ClientWithAllocation[] =>
  options.map(option => normalizeClientOptionByTeamToAllocation(option, existedAllocationId));

export const buildSyncedClientAllocations = (
  selectedClients: ClientWithAllocation[],
  currentAllocations: ClientWithAllocation[] = [],
): ClientWithAllocation[] => {
  const currentById = new Map(currentAllocations.map(client => [client.id, client]));

  return selectedClients.map(selected => currentById.get(selected.id) ?? selected);
};

export const getClientIdsFromAllocations = (allocations: ClientWithAllocation[]): number[] =>
  allocations.map(client => client.id);

export const getAllSelectedClientIds = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[] = [],
): number[] =>
  Array.from(new Set(teamMembersAllocations.flatMap(member => member.selectedClientsIds ?? [])));

export const normalizeLocationGroupOptionToAllocation = (
  locationGroupId: number,
  locationGroupName: string,
): LocationGroupWithAllocation => ({
  locationGroupId,
  locationGroupName,
  allocation: null,
  clientMemos: {},
  clientTeamIds: {},
  removedClientIds: [],
});

export const buildLocationGroupPayloadForAllocation = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
  allocationId: number,
  locationGroups: IAllocationClientsByGroup[],
  includeNewGroups?: boolean,
): { locationGroupIds: number[]; clients: { id: number; memo: string; teamId?: number }[] } => {
  const matchingGroupAllocations = teamMembersAllocations.flatMap(member => {
    const existing = member.locationGroupAllocations.filter(
      lg => lg.allocation?.id === allocationId,
    );
    if (!existing.length) return [];
    if (!includeNewGroups) return existing;
    const newGroups = member.locationGroupAllocations.filter(lg => lg.allocation === null);
    return [...existing, ...newGroups];
  });

  const locationGroupIds = Array.from(
    new Set(matchingGroupAllocations.map(lg => lg.locationGroupId)),
  );

  const clients = matchingGroupAllocations.flatMap(allocationGroup => {
    const locationGroup = locationGroups.find(
      lg => lg.locationGroup.id === allocationGroup.locationGroupId,
    );
    const memos = allocationGroup.clientMemos ?? {};
    const removed = allocationGroup.removedClientIds ?? [];
    const teamIds = allocationGroup.clientTeamIds ?? {};

    return (locationGroup?.clients ?? [])
      .filter(c => !removed.includes(c.id))
      .filter(c => !allocationGroup.allocation || c.id in memos)
      .map(c => {
        const teamId = teamIds[c.id] ?? c.team?.id ?? c.teams?.[0]?.id;

        return {
          id: c.id,
          memo: memos[c.id] ?? '',
          ...(teamId ? { teamId: Number(teamId) } : {}),
        };
      });
  });

  return { locationGroupIds, clients };
};

export const groupLocationGroupAllocationsToAdd = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
  locationGroups: IAllocationClientsByGroup[],
): IAddClientAllocation[] =>
  teamMembersAllocations.reduce<IAddClientAllocation[]>((acc, member) => {
    const newGroups = member.locationGroupAllocations.filter(lg => lg.allocation === null);
    if (!newGroups.length) return acc;

    newGroups.forEach(newGroup => {
      const locationGroup = locationGroups.find(
        lg => lg.locationGroup.id === newGroup.locationGroupId,
      );

      const clients = (locationGroup?.clients ?? [])
        .filter(client => !(newGroup.removedClientIds ?? []).includes(client.id))
        .map(client => ({
          id: client.id,
          memo: newGroup.clientMemos?.[client.id] ?? '',
          ...(newGroup.clientTeamIds?.[client.id]
            ? { teamId: Number(newGroup.clientTeamIds[client.id]) }
            : {}),
        }));

      if (clients.length) {
        acc.push({ userId: member.id, clients, locationGroupIds: [newGroup.locationGroupId] });
      }
    });

    return acc;
  }, []);

export const groupAllocationsToAdd = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
): IAddClientAllocation[] =>
  teamMembersAllocations.reduce<IAddClientAllocation[]>((acc, member) => {
    const clients = member.clientsAllocations
      .filter(client => !client.allocation && !client.allocationId)
      .map(client => {
        const selectedTeamId = client.teamId ?? client.team?.id;
        return {
          id: client.clientId ?? client.id,
          memo: client.memo ?? '',
          ...(selectedTeamId ? { teamId: Number(selectedTeamId) } : {}),
        };
      });

    if (clients.length) {
      acc.push({
        userId: member.id,
        clients,
        locationGroupIds: [],
      });
    }

    return acc;
  }, []);
